(function (window) {
  'use strict';

  // Auth en mode Bearer (cf. contrat API portail client). En cross-origin
  // (Shopify → erp-achat.vercel.app) le cookie de session est bloqué par Safari/iOS ;
  // l'API renvoie donc un JWT dans le body de /auth/login qu'on rejoue en header
  // `Authorization: Bearer <token>` sur chaque appel authentifié. Le token glisse :
  // toute réponse peut renvoyer un JWT rafraîchi dans le header `X-Client-Portal-Token`.
  //
  // Stockage en sessionStorage (+ miroir mémoire) : le token DOIT survivre au
  // window.location.reload() post-login. sessionStorage vs localStorage réduit le
  // PÉRIMÈTRE DE FUITE (pas de partage cross-onglets, effacé à la fermeture de
  // l'onglet) — ce n'est PAS une défense XSS : une XSS active lit la variable ou
  // sniffe le header X-Client-Portal-Token. La vraie défense reste la CSP de la
  // page portail + le cap de durée de vie 24h du JWT (cf. §8 du contrat).
  var TOKEN_STORAGE_KEY = 'bp:token';
  var memToken = null;

  function readStoredToken() {
    if (memToken) return memToken;
    try {
      var v = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (v) memToken = v;
      return v;
    } catch (e) {
      return null;
    }
  }

  function writeStoredToken(token) {
    memToken = token || null;
    try {
      if (token) sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      else sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (e) {
      /* sessionStorage indisponible */
    }
  }

  function ApiClient() {
    var cfg = window.buybackPortalConfig;
    var base = cfg ? cfg.getApiBaseUrl() : 'https://erp-achat.vercel.app/';
    this.baseUrl = base.replace(/\/$/, '');
  }

  ApiClient.prototype._fetch = async function (path, opts) {
    opts = opts || {};
    var cfg = window.buybackPortalConfig;
    var locale = cfg ? cfg.getLocale() : 'fr';
    var token = readStoredToken();
    var headers = Object.assign({ Accept: 'application/json', 'Accept-Language': locale }, opts.headers || {});
    if (token) headers['Authorization'] = 'Bearer ' + token;
    var body = opts.body;
    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(body);
    }
    // Bearer pur : pas de `credentials: 'include'` — on ne dépend plus du cookie
    // cp_session (bloqué cross-site sur Safari/iOS).
    var res = await fetch(this.baseUrl + '/api/client' + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: body,
    });
    // Refresh glissant : toute réponse authentifiée (y compris les GET) peut
    // renvoyer un JWT rafraîchi. L'ignorer = déconnexion silencieuse à 4h.
    var refreshed = res.headers.get('X-Client-Portal-Token');
    if (refreshed) writeStoredToken(refreshed);
    if (res.status === 401) {
      // Token absent/invalide/expiré → purge + retour login (aucun fallback cookie).
      writeStoredToken(null);
      var __hash = window.location.hash || '';
      var __qIdx = __hash.indexOf('?');
      var __query = __qIdx >= 0 ? __hash.slice(__qIdx) : '';
      window.location.hash = '#/login' + __query;
      throw new Error('UNAUTHENTICATED');
    }
    if (res.status === 429) {
      var retryAfter = res.headers.get('Retry-After');
      var err429 = new Error('RATE_LIMITED');
      err429.retryAfter = retryAfter ? Number(retryAfter) : null;
      throw err429;
    }
    // `raw` : renvoyer la réponse brute (ex. contrat PDF) — auth + refresh déjà gérés.
    if (opts.raw) {
      if (!res.ok) {
        var errRaw = new Error('HTTP_' + res.status);
        errRaw.code = 'unknown';
        throw errRaw;
      }
      return res;
    }
    var data = null;
    try {
      data = await res.json();
    } catch (e) {
      /* no body */
    }
    if (!res.ok) {
      var err = new Error('HTTP_' + res.status);
      err.code = (data && data.error) || 'unknown';
      err.data = data;
      throw err;
    }
    return data;
  };

  ApiClient.prototype.login = async function (payload) {
    var data = await this._fetch('/auth/login', { method: 'POST', body: payload });
    // Mode Bearer : on stocke le JWT renvoyé dans le body (le champ `csrf` éventuel
    // est ignoré, inutile hors chemin cookie).
    if (data && typeof data.token === 'string' && data.token) {
      writeStoredToken(data.token);
    }
    return data;
  };
  ApiClient.prototype.logout = async function () {
    // Logout côté client : le JWT est stateless (pas de révocation serveur).
    // L'appel réseau reste best-effort ; seule la purge locale du token compte.
    try {
      await this._fetch('/auth/logout', { method: 'POST' });
    } catch (e) {
      /* best-effort : la purge ci-dessous suffit à déconnecter */
    } finally {
      writeStoredToken(null);
    }
  };
  ApiClient.prototype.me = function () {
    return this._fetch('/me');
  };
  ApiClient.prototype.listReprises = function () {
    return this._fetch('/reprises');
  };
  ApiClient.prototype.getReprise = function (id) {
    return this._fetch('/reprises/' + encodeURIComponent(id));
  };
  ApiClient.prototype.accept = function (id, payload) {
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/accept', { method: 'POST', body: payload });
  };
  ApiClient.prototype.refuse = function (id, payload) {
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/refuse', { method: 'POST', body: payload });
  };
  ApiClient.prototype.uploadDoc = function (id, docType, file) {
    var fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/uploads', { method: 'POST', body: fd });
  };
  ApiClient.prototype.getDocsStatus = function (id) {
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/uploads');
  };
  ApiClient.prototype.getContract = function (id) {
    // Réponse PDF : on récupère la réponse brute pour la lire en blob côté UI
    // (un <a href> classique ne peut pas porter le header Authorization).
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/contract', {
      raw: true,
      headers: { Accept: 'application/pdf' },
    });
  };

  ApiClient.prototype.selectStore = function (id, payload) {
    return this._fetch('/reprises/' + encodeURIComponent(id) + '/select-store', { method: 'POST', body: payload });
  };

  ApiClient.prototype.persistStoreChoice = async function (repriseId, storeId, expectedUpdatedAt, opts) {
    opts = opts || {};
    // Validation client-side du storeId : numérique 1-32 chars OU sentinel 'mint_direct'
    // OU sentinel 'no_store_nearby' (case-sensitive strict pour les deux sentinels)
    var MINT = (window.buybackPortalHelpers && window.buybackPortalHelpers.MINT_DIRECT_STORE_ID) || 'mint_direct';
    var NO_STORE_NEARBY =
      (window.buybackPortalHelpers && window.buybackPortalHelpers.NO_STORE_NEARBY_STORE_ID) || 'no_store_nearby';
    var isValidNumeric = /^[0-9]{1,32}$/.test(String(storeId));
    var isMintDirect = String(storeId) === MINT;
    var isNoStoreNearby = String(storeId) === NO_STORE_NEARBY;
    if (!isValidNumeric && !isMintDirect && !isNoStoreNearby) {
      var errVal = new Error('INVALID_STORE_ID');
      errVal.code = 'invalid_payload';
      throw errVal;
    }
    var payload = { store_id: String(storeId), expectedUpdatedAt: expectedUpdatedAt };
    try {
      var data = await this.selectStore(repriseId, payload);
      try {
        sessionStorage.removeItem('bp:store:' + repriseId);
      } catch (eClean) {
        /* sessionStorage indisponible */
      }
      // STRICT : `reprise` doit être explicitement wrappée par l'API et contenir
      // au moins un champ stable (`id` ou `code_mint`). Le fallback `|| data`
      // précédent renvoyait des payloads partiels (ex {ok:true, updatedAt:...})
      // qui faisaient muter wholesale `_currentReprise` côté détail → UUID brut
      // dans la ref, écran pending faute d'`offer.price`, timeline 1/1.
      var wrappedReprise = data && data.reprise;
      var isUsableReprise =
        wrappedReprise && typeof wrappedReprise === 'object' && (wrappedReprise.id || wrappedReprise.code_mint);
      // Toujours exposer le nouvel updatedAt à l'appelant pour refresh
      // optimiste du state local (évite 409 stale_data sur mutation suivante).
      var nextUpdatedAt = (wrappedReprise && wrappedReprise.updatedAt) || (data && data.updatedAt) || null;
      return {
        ok: true,
        reprise: isUsableReprise ? wrappedReprise : null,
        updatedAt: nextUpdatedAt,
        source: 'erp',
      };
    } catch (err) {
      var msg = err && err.message ? err.message : '';
      // 404/405 — endpoint absent côté ERP → fallback sessionStorage
      if (msg === 'HTTP_404' || msg === 'HTTP_405') {
        try {
          sessionStorage.setItem('bp:store:' + repriseId, String(storeId));
        } catch (e) {}
        if (window.console && window.console.warn) {
          console.warn('[buyback] select-store endpoint absent, fallback sessionStorage');
        }
        return { ok: true, reprise: null, source: 'session' };
      }
      // 409 — stale_data : refetch + retry une fois.
      // GET /reprises/[id] retourne { ok, reprise: { ..., updatedAt } }, donc
      // lire `fresh.reprise.updatedAt` (le top-level `fresh.updatedAt` était
      // toujours undefined → retry silencieusement échoué → 409 remontait).
      if (msg === 'HTTP_409' && !opts.__retry) {
        try {
          var fresh = await this.getReprise(repriseId);
          var freshReprise = fresh && fresh.reprise;
          var freshUpdatedAt = (freshReprise && freshReprise.updatedAt) || (fresh && fresh.updatedAt) || null;
          if (freshUpdatedAt) {
            return await this.persistStoreChoice(repriseId, storeId, freshUpdatedAt, { __retry: true });
          }
        } catch (e2) {
          /* propage erreur originale */
        }
        throw err;
      }
      // 403 — en Bearer pur, csrf_invalid ne doit plus survenir (cf. §6 du contrat) ;
      // un 403 éventuel est une vraie erreur d'autorisation → propagée telle quelle.
      // 422 — wrong_status : expose currentStatus pour UI + force re-render
      // 429 — rate_limited : propage tel quel (retryAfter déjà sur err)
      // 400 — invalid_payload : ne devrait pas arriver (validation client-side ci-dessus)
      // 500 — server error : propage générique
      throw err;
    }
  };

  ApiClient.prototype.selectPickupDate = async function (repriseId, payload) {
    return this._fetch('/reprises/' + encodeURIComponent(repriseId) + '/select-pickup-date', {
      method: 'POST',
      body: payload,
    });
  };
  ApiClient.prototype.persistPickupDate = async function (repriseId, params, opts) {
    params = params || {};
    opts = opts || {};
    // Garde-fou défensif : la reprise PART (boutique) n'a pas de créneau d'enlèvement.
    // L'UI ne doit jamais ouvrir cette modale pour PART — si elle le fait, on refuse côté client.
    if (params.isPart === true) {
      var errPart = new Error('FORBIDDEN_FOR_PART');
      errPart.code = 'forbidden_for_part';
      throw errPart;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(params.isoDate))) {
      var errVal = new Error('INVALID_DATE');
      errVal.code = 'invalid_payload';
      throw errVal;
    }
    if (params.slot !== 'matin' && params.slot !== 'apremidi') {
      var errSlot = new Error('INVALID_SLOT');
      errSlot.code = 'invalid_payload';
      throw errSlot;
    }
    var payload = {
      pickup_requested_date: String(params.isoDate),
      pickup_requested_slot: String(params.slot),
      expectedUpdatedAt: params.expectedUpdatedAt,
    };
    try {
      var data = await this.selectPickupDate(repriseId, payload);
      var wrapped = data && data.reprise;
      var nextUpdatedAt = (wrapped && wrapped.updatedAt) || (data && data.updatedAt) || null;
      return { ok: true, reprise: wrapped || null, updatedAt: nextUpdatedAt };
    } catch (err) {
      var msg = err && err.message ? err.message : '';
      if (msg === 'HTTP_409' && !opts.__retry) {
        try {
          var fresh = await this.getReprise(repriseId);
          var freshUpdatedAt = (fresh && fresh.reprise && fresh.reprise.updatedAt) || null;
          if (freshUpdatedAt) {
            var retryParams = Object.assign({}, params, { expectedUpdatedAt: freshUpdatedAt });
            return await this.persistPickupDate(repriseId, retryParams, { __retry: true });
          }
        } catch (e2) {
          /* propage erreur originale */
        }
        throw err;
      }
      // 403 — en Bearer pur, csrf_invalid ne doit plus survenir (cf. §6) → propagé.
      throw err;
    }
  };

  window.BuybackApiClient = ApiClient;
  window.buybackApi = new ApiClient();
})(window);
